import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Plus, X, Layers, AlertTriangle, Lock, Send, Trash2, ShieldCheck, Paperclip } from "lucide-react";
import { PdfLangMenu } from "@/components/PdfLangMenu";
import { masterManifestPDF } from "@/lib/masterManifestPdf";
import { CountrySelect, ProvinceSelect } from "@/components/GeoSelect";
import PhotoUploader from "@/components/PhotoUploader";

const MODES = ["aereo", "terrestre", "maritimo", "express"];
const MODE_KEY = { aereo: "air", terrestre: "road", maritimo: "sea", express: "express" };
const MODE_TONE = { aereo: "bg-sky-600", terrestre: "bg-amber-600", maritimo: "bg-cyan-700", express: "bg-red-600" };
const STATE_TONE = {
  abierto: "bg-amber-100 text-amber-700", cerrado: "bg-slate-900 text-white",
  en_transito: "bg-blue-100 text-blue-700", recibido_destino: "bg-violet-100 text-violet-700",
  entregado: "bg-emerald-100 text-emerald-700",
};
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { origen: "", destino: "", destino_pais: "", destino_ciudad: "", modo: "aereo", transportista: "", fecha: today(), transporte_ref: "", etd: "", eta: "", notas: "" };

export default function Masters() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const canManage = ["admin", "operaciones", "almacen"].includes(user.role);
  const canReceiveInbound = user.role === "sucursal" && !!user.main;
  const isAdmin = user.role === "admin";

  const [rows, setRows] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [codes, setCodes] = useState("");
  const [force, setForce] = useState(false);
  const [rejected, setRejected] = useState([]);
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/masters").then(r => setRows(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Origins are the real hubs/branches; destinations come from the country catalogue
    Promise.all([
      api.get("/warehouses").then(r => (r.data || []).map(w => w.nombre)).catch(() => []),
      api.get("/oficinas").then(r => r.data.oficinas || []).catch(() => []),
    ]).then(([wh, of]) => setOrigins([...new Set([...wh, ...of].filter(Boolean))]));
    api.get("/countries").then(r => setCountries(r.data || [])).catch(() => {});
  }, [api]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/masters/${id}`);
      setDetail(data); setRejected([]);
      if (data.estado === "abierto") {
        const av = await api.get("/packages", { params: { disponible_para: "master", modo: data.modo } });
        setAvailable(av.data || []);
      } else setAvailable([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/masters", form);
      toast.success(`${t("saved")} · ${data.codigo}`);
      setShowForm(false); setForm(EMPTY); await load(); openDetail(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const addPackages = async (list) => {
    const arr = (list || codes.split(/[\s,;]+/)).map(c => c.trim()).filter(Boolean);
    if (!arr.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/masters/${detail.id}/packages`, { codes: arr, force });
      setRejected(data.rejected); setCodes("");
      if (data.added.length) toast.success(`${t("added_ok")}: ${data.added.length}`);
      data.rejected.forEach(r => toast.error(`${r.code}: ${r.motivo}`, { duration: 8000 }));
      await openDetail(detail.id); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const removePkg = async (code) => {
    try { await api.delete(`/masters/${detail.id}/packages/${encodeURIComponent(code)}`); await openDetail(detail.id); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const closeMaster = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/masters/${detail.id}/close`);
      toast.success(`${t("close_master")} · ${data.totales.paquetes} · ${data.totales.peso} kg`);
      await openDetail(detail.id); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const setState = async (estado) => {
    try {
      await api.post(`/masters/${detail.id}/state`, { estado, nota: "" });
      toast.success(t(`master_${estado}`) || estado);
      await openDetail(detail.id); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const printManifest = async (m, lang) => {
    try {
      const { data } = await api.get(`/masters/${m.id}/manifest`);
      await masterManifestPDF(data, lang);
      toast.success("PDF");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-teal-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  return (
    <div className="space-y-4" data-testid="masters-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-teal-600" /> {t("masters")}</h2>
          <p className="text-sm text-slate-500">{rows.length}</p>
        </div>
        {canManage && (
          <button data-testid="new-master-btn" onClick={() => { setForm(EMPTY); setShowForm(true); }} className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("new_master")}
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid="master-hint">{t("master_hint")}</p>

      {rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400" data-testid="no-masters">
          <Layers className="w-14 h-14 mx-auto opacity-30 mb-3" /><p>{t("no_masters")}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-3 text-start">{t("master")}</th>
                <th className="p-3 text-start">{t("date")}</th>
                <th className="p-3 text-start">{t("origin")} → {t("destination")}</th>
                <th className="p-3 text-start">{t("transport_mode")}</th>
                <th className="p-3 text-start">{t("transport_ref")}</th>
                <th className="p-3 text-end">{t("total_packages")}</th>
                <th className="p-3 text-end">{t("total_weight")}</th>
                <th className="p-3 text-end">CBM</th>
                <th className="p-3 text-start">{t("status")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(m => (
                <tr key={m.id} data-testid={`master-row-${m.codigo}`}>
                  <td className="p-3 font-mono font-semibold">{m.codigo}</td>
                  <td className="p-3 tabular-nums text-slate-600">{m.fecha}</td>
                  <td className="p-3">{m.origen || "—"} → <b>{[m.destino_ciudad, m.destino_pais].filter(Boolean).join(", ") || m.destino || "—"}</b></td>
                  <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white ${MODE_TONE[m.modo]}`}>{t(MODE_KEY[m.modo])}</span></td>
                  <td className="p-3 text-slate-600">{m.transporte_ref || "—"}</td>
                  <td className="p-3 text-end tabular-nums">{m.totales?.paquetes ?? 0}</td>
                  <td className="p-3 text-end tabular-nums">{m.totales?.peso ?? 0} kg</td>
                  <td className="p-3 text-end tabular-nums">{m.totales?.cbm ?? 0}</td>
                  <td className="p-3"><span data-testid={`master-state-${m.codigo}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATE_TONE[m.estado] || "bg-slate-100"}`}>{t(`master_${m.estado}`) || m.estado}</span></td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <PdfLangMenu testid={`master-manifest-${m.codigo}`} onPrint={(lg) => printManifest(m, lg)} label={t("master_manifest")} />
                      <button data-testid={`master-detail-${m.codigo}`} onClick={() => openDetail(m.id)} className="text-xs font-semibold text-teal-600 hover:underline">{t("open_detail")}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-xl w-full max-w-lg p-6 space-y-3" data-testid="master-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">{t("new_master")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div>
              <label className={lbl}>{t("transport_mode")} *</label>
              <div className="grid grid-cols-4 gap-2" data-testid="mf-modo">
                {MODES.map(m => (
                  <button key={m} type="button" data-testid={`mf-modo-${m}`} onClick={() => setForm({ ...form, modo: m })}
                    className={`py-2 rounded-lg text-xs font-semibold border ${form.modo === m ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-300"}`}>{t(MODE_KEY[m])}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("origin")} *</label>
                <select required data-testid="mf-origen" value={form.origen} onChange={(e) => setForm({ ...form, origen: e.target.value })} className={inp}>
                  <option value="">—</option>
                  {origins.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>{t("dest_country")} *</label>
                <CountrySelect required countries={countries} testid="mf-destino-pais" className={inp}
                  value={form.destino_pais}
                  onChange={(v) => setForm({ ...form, destino_pais: v, destino_ciudad: "", destino: v })} />
              </div>
              <div>
                <label className={lbl}>{t("dest_city")}</label>
                <ProvinceSelect countries={countries} country={form.destino_pais} testid="mf-destino" className={inp}
                  value={form.destino_ciudad}
                  onChange={(v) => setForm({ ...form, destino_ciudad: v, destino: v || form.destino_pais })} />
              </div>
              <div><label className={lbl}>{t("carrier")}</label><input data-testid="mf-carrier" value={form.transportista} onChange={(e) => setForm({ ...form, transportista: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("transport_ref")}</label><input data-testid="mf-ref" value={form.transporte_ref} onChange={(e) => setForm({ ...form, transporte_ref: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("date")}</label><input type="date" data-testid="mf-fecha" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inp} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className={lbl}>{t("etd")}</label><input type="date" data-testid="mf-etd" value={form.etd} onChange={(e) => setForm({ ...form, etd: e.target.value })} className={inp} /></div>
                <div><label className={lbl}>{t("eta")}</label><input type="date" data-testid="mf-eta" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} className={inp} /></div>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" disabled={busy} data-testid="mf-submit" className="flex-1 bg-teal-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-3 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-5xl p-5 space-y-4 my-4" data-testid="master-detail">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-display font-semibold text-lg flex items-center gap-2">
                  <span className="font-mono">{detail.codigo}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white ${MODE_TONE[detail.modo]}`}>{t(MODE_KEY[detail.modo])}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATE_TONE[detail.estado] || "bg-slate-100"}`}>{t(`master_${detail.estado}`) || detail.estado}</span>
                </h3>
                <p className="text-xs text-slate-500">{detail.origen} → {[detail.destino_ciudad, detail.destino_pais].filter(Boolean).join(", ") || detail.destino} · {detail.transportista} · {detail.transporte_ref} · ETD {detail.etd || "—"} · ETA {detail.eta || "—"}</p>
              </div>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="master-totals">
              {[[t("total_packages"), detail.totales?.paquetes ?? 0], [t("total_units"), detail.totales?.unidades ?? 0],
                [t("total_weight"), `${detail.totales?.peso ?? 0} kg`], [t("total_cbm"), detail.totales?.cbm ?? 0]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-teal-200 bg-teal-50 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700 opacity-80">{k}</div>
                  <div className="mt-1 text-xl font-semibold tabular-nums text-teal-900">{v}</div>
                </div>
              ))}
            </div>

            {detail.estado === "abierto" && canManage && (
              <div className="border-2 border-teal-200 bg-teal-50/40 rounded-lg p-3 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-teal-800">{t("add_to_master")}</p>
                <div className="flex gap-2 flex-wrap">
                  <input data-testid="master-add-input" value={codes} onChange={(e) => setCodes(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPackages(); } }}
                    placeholder={t("scan_or_type")} className={`${inp} flex-1 min-w-[220px] font-mono`} />
                  <button data-testid="master-add-btn" disabled={busy} onClick={() => addPackages()} className="bg-teal-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> {t("add")}</button>
                  <button data-testid="master-close-btn" disabled={busy || !(detail.packages || []).length} onClick={closeMaster} className="bg-slate-900 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5"><Lock className="w-4 h-4" /> {t("close_master")}</button>
                </div>
                {isAdmin && (
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" data-testid="master-force" checked={force} onChange={(e) => setForce(e.target.checked)} />
                    <ShieldCheck className="w-3.5 h-3.5 text-amber-600" /> {t("force_admin")}
                  </label>
                )}
                {rejected.length > 0 && (
                  <div className="space-y-1" data-testid="master-rejected">
                    {rejected.map(r => (
                      <div key={r.code} className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-2 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span><b className="font-mono">{r.code}</b> — {r.motivo}</span>
                      </div>
                    ))}
                  </div>
                )}
                {available.length > 0 && (
                  <div className="pt-1" data-testid="master-available">
                    <p className="text-[11px] text-slate-500 mb-1">{t("verified")} · {t("packages")}: {available.length}</p>
                    <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                      {available.map(p => (
                        <button key={p.codigo} data-testid={`master-av-${p.codigo}`} onClick={() => addPackages([p.codigo])}
                          className="text-[11px] font-mono border border-teal-300 bg-white hover:bg-teal-100 rounded px-2 py-1">
                          {p.codigo} · {p.destino_ciudad || "—"} · {p.peso_total}kg
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {detail.estado !== "abierto" && canManage && (
              <div className="flex gap-2 flex-wrap">
                {["en_transito", "recibido_destino", "entregado"].map(st => (
                  <button key={st} data-testid={`master-state-btn-${st}`} onClick={() => setState(st)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 ${detail.estado === st ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600"}`}>
                    <Send className="w-3.5 h-3.5" /> {t(`master_${st}`)}
                  </button>
                ))}
                <PdfLangMenu testid={`master-detail-manifest-${detail.codigo}`} onPrint={(lg) => printManifest(detail, lg)} label={t("master_manifest")} />
              </div>
            )}

            {/* Head office (sucursal + main) can mark inbound master as arrived / delivered */}
            {detail.estado !== "abierto" && !canManage && canReceiveInbound && detail.inbound && (
              <div className="flex gap-2 flex-wrap border-2 border-red-200 bg-red-50 rounded-lg p-3">
                <p className="w-full text-xs font-bold uppercase tracking-wider text-red-700">
                  {t("inbound_master_actions") || "Este envío viene hacia tu oficina"}
                </p>
                {["recibido_destino", "entregado"].map(st => (
                  <button key={st} data-testid={`master-state-btn-${st}`} onClick={() => setState(st)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 ${detail.estado === st ? "bg-red-600 text-white border-red-600" : "bg-white border-red-300 text-red-700"}`}>
                    <Send className="w-3.5 h-3.5" /> {t(`master_${st}`)}
                  </button>
                ))}
                <PdfLangMenu testid={`master-detail-manifest-${detail.codigo}`} onPrint={(lg) => printManifest(detail, lg)} label={t("master_manifest")} />
              </div>
            )}

            {/* Read-only PDF download for other viewers (sucursal sub-branches, ventas) */}
            {detail.estado !== "abierto" && !canManage && !canReceiveInbound && (
              <div className="flex justify-end">
                <PdfLangMenu testid={`master-detail-manifest-${detail.codigo}`} onPrint={(lg) => printManifest(detail, lg)} label={t("master_manifest")} />
              </div>
            )}

            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs min-w-[860px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="p-2 text-start">#</th>
                    <th className="p-2 text-start">{t("internal_tracking")}</th>
                    <th className="p-2 text-start">{t("carrier_tracking")}</th>
                    <th className="p-2 text-start">{t("carrier")}</th>
                    <th className="p-2 text-start">{t("sender")}</th>
                    <th className="p-2 text-start">{t("receiver")}</th>
                    <th className="p-2 text-start">{t("dest_city")}</th>
                    <th className="p-2 text-end">{t("units")}</th>
                    <th className="p-2 text-end">{t("weight_kg")}</th>
                    <th className="p-2 text-end">CBM</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(detail.packages || []).length === 0 && <tr><td colSpan={11} className="p-6 text-center text-slate-400">—</td></tr>}
                  {(detail.packages || []).map((p, idx) => {
                    const r = p.recibido || {};
                    return (
                      <tr key={p.codigo} data-testid={`master-pkg-${p.codigo}`}>
                        <td className="p-2 text-slate-400">{idx + 1}</td>
                        <td className="p-2 font-mono">{p.codigo}</td>
                        <td className="p-2 font-mono text-slate-600">{p.tracking_transportista || "—"}</td>
                        <td className="p-2">{p.transportista || "—"}</td>
                        <td className="p-2">{p.remitente_nombre}</td>
                        <td className="p-2">{p.receptor_nombre}</td>
                        <td className="p-2">{p.destino_ciudad || p.destino || "—"}</td>
                        <td className="p-2 text-end tabular-nums">{r.unidades ?? p.numero_paquetes}</td>
                        <td className="p-2 text-end tabular-nums">{r.peso ?? p.peso_total}</td>
                        <td className="p-2 text-end tabular-nums">{r.cbm ?? p.cbm}</td>
                        <td className="p-2 text-end">
                          {detail.estado === "abierto" && canManage && <button data-testid={`master-pkg-del-${p.codigo}`} onClick={() => removePkg(p.codigo)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Attachments: manifests, scans, bills of lading, invoices, etc. */}
            {canManage && (
              <div className="pt-1">
                <div className="flex items-center gap-2 mb-2">
                  <Paperclip className="w-4 h-4 text-teal-600" />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{t("attachments") || "Adjuntos"}</span>
                </div>
                <PhotoUploader
                  scope="master"
                  scopeId={detail.id}
                  value={detail.photo_ids || []}
                  onChange={async (ids) => {
                    try {
                      await api.patch(`/masters/${detail.id}/attachments`, { photo_ids: ids });
                      setDetail({ ...detail, photo_ids: ids });
                    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                  }}
                  max={20}
                  label={t("attachments") || "Adjuntos"}
                  accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                  testid="master-attach"
                />
              </div>
            )}

            <div className="flex justify-end">
              <button onClick={() => setDetail(null)} className="border border-slate-300 px-4 py-2 rounded-lg font-semibold text-sm text-slate-700">{t("close")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
